import React, { useState, useEffect, useMemo } from 'react';
import api, { absUrl } from '../../services/api';
import { useChatStore } from '../../hooks/useChatStore';
import { getAvatarEmoji } from '../Chat/ChatWindow';
import '../../styles/chat.css';

export default function UserInfoPanel({ userId, chatId, user, onClose }) {
  const [userInfo, setUserInfo] = useState(null);
  const [sharedFiles, setSharedFiles] = useState({ images: [], videos: [], audio: [], files: [] });
  const [loading, setLoading] = useState(true);
  const [blocking, setBlocking] = useState(false);
  const [deletingChat, setDeletingChat] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [mediaTab, setMediaTab] = useState('images');
  const deleteChat = useChatStore((s) => s.deleteChat);
  const fetchChats = useChatStore((s) => s.fetchChats);
  const setActiveChat = useChatStore((s) => s.setActiveChat);
  const chatMessages = useChatStore((s) => s.messages?.[chatId]);

  // Медиа, чьи сообщения уже удалены в чате, не показываем.
  // Нужно, т.к. панель загружает shared-files один раз, а сообщения
  // могут удаляться (в т.ч. через WebSocket message_deleted) при открытой панели.
  const visibleFiles = useMemo(() => {
    const deletedIds = new Set(
      (chatMessages || []).filter((m) => m?.isDeleted).map((m) => m.id)
    );
    if (deletedIds.size === 0) return sharedFiles;
    const notDeleted = (f) => !f?.messageId || !deletedIds.has(f.messageId);
    return {
      images: (sharedFiles.images || []).filter(notDeleted),
      videos: (sharedFiles.videos || []).filter(notDeleted),
      audio: (sharedFiles.audio || []).filter(notDeleted),
      files: (sharedFiles.files || []).filter(notDeleted),
    };
  }, [sharedFiles, chatMessages]);

  const gotoMessage = (f) => {
    if (f?.messageId) {
      window.__pendingMsg = { chatId, messageId: f.messageId };
      setActiveChat(chatId);
      onClose();
      setTimeout(() => window.dispatchEvent(new Event('scrollToMessage')), 150);
    } else {
      window.open(absUrl(f.fileUrl), '_blank');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [userRes, filesRes, meRes] = await Promise.all([
          api.get(`/users/${userId}`),
          api.get(`/chats/${chatId}/shared-files`),
          api.get('/users/me'),
        ]);
        setUserInfo(userRes.data);
        setSharedFiles(filesRes.data || { images: [], videos: [], audio: [], files: [] });
        setIsBlocked((meRes.data?.blockedUserIds || []).includes(userId));
      } catch (e) {
        console.error('Failed to load user info:', e);
      }
      setLoading(false);
    };
    load();
  }, [userId, chatId]);

  const avatarSrc = userInfo?.avatarUrl ? absUrl(userInfo.avatarUrl) : null;
  const isSelf = userInfo?.id === user?.id;

  const toggleBlock = async () => {
    setBlocking(true);
    try {
      if (isBlocked) {
        await api.delete(`/users/${userId}/block`);
        setIsBlocked(false);
      } else {
        await api.put(`/users/${userId}/block`);
        setIsBlocked(true);
      }
      fetchChats().catch(() => {});
    } catch (e) {
      console.error('Block action failed:', e);
    }
    setBlocking(false);
  };

  const handleDeleteChat = async () => {
    if (!window.confirm('Удалить чат? Это действие нельзя отменить.')) return;
    setDeletingChat(true);
    try {
      await deleteChat(chatId);
      onClose();
    } catch (e) {
      console.error('Delete chat failed:', e);
    }
    setDeletingChat(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal user-info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-info-header">
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="user-info-avatar" onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
          ) : (
            <div className="user-info-avatar user-info-avatar-placeholder">
              {getAvatarEmoji(userInfo?.displayName || userInfo?.username)}
            </div>
          )}
          <div className="user-info-titles">
            <h3>{userInfo?.displayName || userInfo?.username || 'Загрузка...'}</h3>
            <span className="user-info-username">@{userInfo?.username || ''}</span>
            <span className="user-info-email">{isSelf ? userInfo?.email : ''}</span>
          </div>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="user-info-body">
          {loading && <div className="sticker-picker-loading">Загрузка...</div>}
          {!loading && userInfo?.statusText && (
            <div className="user-info-status">📋 {userInfo.statusText}</div>
          )}
          {!loading && userInfo?.lastSeen && (
            <div className="user-info-status muted">📅 В сети: {new Date(userInfo.lastSeen).toLocaleString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
          )}

          {!isSelf && !loading && (
            <div className="user-info-actions">
              <button
                className={`user-info-btn ${isBlocked ? 'danger' : ''}`}
                onClick={toggleBlock}
                disabled={blocking}
              >
                {blocking ? '...' : isBlocked ? 'Разблокировать' : 'Заблокировать'}
              </button>
              <button className="user-info-btn danger" onClick={handleDeleteChat} disabled={deletingChat}>
                {deletingChat ? '...' : 'Удалить чат'}
              </button>
            </div>
          )}

          <div className="media-tabs">
            {['images', 'videos', 'audio', 'files'].map((t) => (
              <button
                key={t}
                className={`media-tab ${mediaTab === t ? 'active' : ''}`}
                onClick={() => setMediaTab(t)}
              >
                {t === 'images' ? 'Изображения' : t === 'videos' ? 'Видео' : t === 'audio' ? 'Аудио' : 'Файлы'}
                <span className="media-tab-count">
                  {visibleFiles[t]?.length || 0}
                </span>
              </button>
            ))}
          </div>

          {mediaTab === 'images' && (visibleFiles.images?.length > 0 ? (
            <div className="shared-files-section">
              <div className="shared-files-images">
                {visibleFiles.images.slice(0, 24).map((f) => (
                  <img key={f.id} src={absUrl(f.fileUrl)} alt={f.fileName} className="shared-file-img"
                    title={f.fileName} onClick={() => gotoMessage(f)} />
                ))}
              </div>
            </div>
          ) : (
            <div className="user-info-status muted">Нет изображений</div>
          ))}
          {mediaTab === 'videos' && (visibleFiles.videos?.length > 0 ? (
            <div className="shared-files-section">
              <div className="shared-files-list">
                {visibleFiles.videos.slice(0, 10).map((f) => (
                  <a key={f.id} className="shared-file-link" href="#!" onClick={(e) => { e.preventDefault(); gotoMessage(f); }}>
                    {f.fileName}
                  </a>
                ))}
              </div>
            </div>
          ) : (
            <div className="user-info-status muted">Нет видео</div>
          ))}
          {mediaTab === 'audio' && (visibleFiles.audio?.length > 0 ? (
            <div className="shared-files-section">
              <div className="shared-files-list">
                {visibleFiles.audio.slice(0, 10).map((f) => (
                  <a key={f.id} className="shared-file-link" href="#!" onClick={(e) => { e.preventDefault(); gotoMessage(f); }}>
                    {f.fileName}
                  </a>
                ))}
              </div>
            </div>
          ) : (
            <div className="user-info-status muted">Нет аудио</div>
          ))}
          {mediaTab === 'files' && (visibleFiles.files?.length > 0 ? (
            <div className="shared-files-section">
              <div className="shared-files-list">
                {visibleFiles.files.slice(0, 15).map((f) => (
                  <a key={f.id} className="shared-file-link" href="#!" onClick={(e) => { e.preventDefault(); gotoMessage(f); }}>
                    {f.fileName} <span className="shared-file-size">({Math.round(f.fileSize / 1024)} KB)</span>
                  </a>
                ))}
              </div>
            </div>
          ) : (
            <div className="user-info-status muted">Нет файлов</div>
          ))}
        </div>
      </div>
    </div>
  );
}
